/* eslint-disable no-restricted-syntax */ // TODO REMOVE
/* eslint-disable @typescript-eslint/no-explicit-any */
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import { changeCookieBannerLanguage } from "@pcf-engineering/gatsby-plugin-pcs-cookie-banner";
import { useLocation } from '@reach/router';

import theme from '../../themes/phoenix';
import GlobalStyles from '../../themes/phoenix/globalStyles';
import SkipToContent from '../SkipToContent';
// Depends on https://pc-technology.atlassian.net/browse/PCIPS-1743
import Header from '../Header';
import Footer from '../Footer';
import SEO from '../Seo';
import Redirect from '../redirect';
import useBreadcrumbListSchema from '../../hooks/useBreadcrumbListSchema';
import { excludeProps } from '../../utils/fixPropWarnings';
import LegalDisclaimer from '../LegalDisclaimer';
import defaultLightTheme from '../../themes/defaultLightTheme';
import NewGlobalStyles from '../../themes/globalStyles';
import { useBlade } from '../../state/BladeProvider';
import RegionLockedContent from '../RegionLockedContent';
import { Provinces, useProvince } from '../../state/ProvinceProvider';
import { ModalType } from '../../state/ModalProvider';
import Modals from '../Modals';

/**
 * usePageData - Transforms data from the CMS into a usable format.
 * Currently supports the following content types:
 *  - ContentfulPage
 *  - ContentfulSplashPage
 *  - ContentfulAutoRegionalPage
 *  - ContentfulHomeRegionalPage
 *  - ContentfulSitemap
 *  - ContentfulBlogCategory
 *  - ContentfulBlogPost
 *  - ContentfulNewsArticle
 * @param {Object} data - The contentful entry data from the CMS
 * @returns {Object} An object containing all of the transformed data in an easily digestible format
 */

// TODO remove once theme refactor done
function isObject(item: any): item is Record<string, any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

function mergeDeep<T>(target: T, ...sources: any[]): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}
export const usePageData = (data: any) => useMemo(() => ({
  customHeader: data?.optionalCustomPageHeader || data?.customSplashPageHeader,
  customLegal: data?.optionalLegal,
  customNotifications: data?.topNotificationBanner || [],
  customSchema: data?.schema?.json || {},
  breadcrumbs: data?.breadCrumbs,
  canLanguageToggle: !!(data?.canLanguageToggle || data?.canLanguageToggleThisPage),
  canSearchIndex: data?.splashPageIndex !== false,
  title: data?.metaTitle
    || data?.pageMetaTitle
    || data?.pageName
    || data?.displayName
    || data?.internalName,
  description: data?.metaDescription?.metaDescription
    || data?.metaDescriptionText?.metaDescriptionText
    || data?.pageMetaDescription?.pageMetaDescription,
  name: data?.displayName
    || data?.pageName
    || data?.metaTitle
    || data?.pageMetaTitle
    || data?.internalName,
  hidePage: data?.hidePage ?? false,
}), [data]);

/**
 * Page - Wraps a single page on the site. Renders a header, footer, <main> tag, "back to top" button, and more. Most pages (if not all) should implement this component.
 * @param {Object} data - The page data from contentful. Supports all the content types listed in usePageData above.
 * @param {Boolean} showSearchBar - Whether or not to show the search bar in the header. Defaults to true.
 * @param {Boolean} showBackToTop - Whether or not to show the back to top button on the page. Defaults to false.
 * @param {Boolean} noIndex - Whether or not to prevent search crawlers from indexing the page for search engines. Defaults to false.
 * @param {Array} meta - An array of objects to render as meta tags.
 * @param {Array} ldJson - An array of objects to render as ld+json schema data.
 */

interface PageProps {
  data: any;
  showSearchBar?: boolean;
  showBackToTop?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  noIndex?: boolean;
  meta?: any[];
  ldJson?: any[];
  children: React.ReactNode;
  themeType?: 'old' | 'merged' | 'new';
  provinceOverride?: Provinces
}

const Page = ({
  data,
  showSearchBar = true,
  showBackToTop = false,
  showHeader = true,
  showFooter = true,
  noIndex = false,
  meta = [],
  ldJson = [],
  themeType = 'old',
  children,
  provinceOverride,
}: PageProps) => {
  const { locale } = useIntl();
  const {
    customHeader,
    customLegal,
    customNotifications,
    breadcrumbs,
    canLanguageToggle,
    canSearchIndex,
    title,
    description,
    name,
    hidePage,
  } = usePageData(data);
  const { setActiveBlade } = useBlade();
  const location = useLocation(); // Get current location

  useEffect(() => {
    // When location changes, set active blade to null
    setActiveBlade(null);
  }, [location, setActiveBlade]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !window.location.hash) window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    changeCookieBannerLanguage(locale);
  }, [locale]);

  const { setProvinceOverride } = useProvince();
  setProvinceOverride(provinceOverride || null);

  const breadcrumbListLdJson = useBreadcrumbListSchema(breadcrumbs, name);

  if ((!canLanguageToggle && locale !== 'en') || hidePage) return <Redirect to="/" />;
  // TODO Remove once old theme is gone
  const mergedTheme = mergeDeep(defaultLightTheme, theme);
  const currentTheme = (themeType === 'new' && defaultLightTheme)
    || (themeType === 'merged' && mergedTheme)
    || theme;
  const pageContent = (
    <StyleSheetManager shouldForwardProp={excludeProps()}>
      <ThemeProvider theme={currentTheme}>
        {themeType === 'old' ? <GlobalStyles /> : <NewGlobalStyles />}
        <SkipToContent />

        <SEO
          title={title}
          description={description}
          metaTags={meta}
          ldJson={[breadcrumbListLdJson, ...ldJson]}
          noIndex={noIndex || !canSearchIndex}
        />

        {showHeader && (
          <Header
            customHeader={customHeader}
            customNotifications={customNotifications}
            search={showSearchBar}
          />
        )}
        <main id="main-content">
          {children}
          {customLegal && <LegalDisclaimer data={customLegal} />}
          <Modals />
        </main>

        {showFooter && (
          <Footer backToTop={showBackToTop} />
        )}
      </ThemeProvider>
    </StyleSheetManager>
  );

  return data?.regionLockedContent ? (
    <RegionLockedContent redirect={`/${locale}/?modal=${ModalType.PAGE_NOT_AVAILABLE_IN_PROVINCE}`} data={data.regionLockedContent}>
      {pageContent}
    </RegionLockedContent>
  ) : (
    pageContent
  );
};

export default Page;
